import { useRef, useState, useEffect } from 'react';
import { Tooltip } from './Tooltip';
import { useNetlifyLogs } from '~/lib/hooks/useNetlifyLogs';
import { classNames } from '~/utils/classNames';

interface NetlifyLogsButtonProps {
  deployedId: string | null;
  accessToken: string | null | undefined;
  deployStatus?: string;
  autoExpand?: boolean;
  adminUrl?: string | null;
}

export const NetlifyLogsButton = ({
  deployedId,
  accessToken,
  deployStatus,
  autoExpand = false,
}: NetlifyLogsButtonProps) => {
  const logsRef = useRef<HTMLDivElement>(null);
  const [isExpanded, setIsExpanded] = useState(autoExpand);

  const {
    logs,
    isConnecting: isConnectingToLogs,
    isViewingLogs,
    connectToLogs,
    safeConnectToLogs,
    formatTimestamp,
    formatLogMessage,
    setIsViewingLogs,
  } = useNetlifyLogs(deployedId, accessToken);

  // Auto-expand logs when autoExpand prop changes
  useEffect(() => {
    if (autoExpand && deployStatus !== 'error') {
      console.log('[NetlifyLogsButton] Auto-expanding logs, deployStatus:', deployStatus);
      setIsExpanded(true);
      setIsViewingLogs(true);
    }
  }, [autoExpand, deployStatus, setIsViewingLogs]);

  // Scroll to bottom when new logs arrive
  useEffect(() => {
    if (logsRef.current && logs.length > 0) {
      logsRef.current.scrollTop = logsRef.current.scrollHeight;
    }
  }, [logs]);

  // Toggle logs visibility
  const toggleLogs = () => {
    console.log('[NetlifyLogsButton] Toggling logs visibility, current state:', {
      isViewingLogs,
      isExpanded,
      connecting: isConnectingToLogs,
      existingLogs: logs.length,
    });

    // Only connect if not already viewing and we're expanding
    if (!isViewingLogs && !isExpanded) {
      // Check if we already have logs or are already connecting before attempting to connect
      if (logs.length === 0 && !isConnectingToLogs) {
        console.log('[NetlifyLogsButton] No logs available, initiating connection');
        safeConnectToLogs();
      } else {
        // Just set viewing state if we already have logs or are connecting
        console.log('[NetlifyLogsButton] Logs already available or connecting, just setting viewing state');
        setIsViewingLogs(true);
      }
    }

    setIsExpanded(!isExpanded);
  };

  // Render logs content
  const renderLogsContent = () => (
    <div
      ref={logsRef}
      className="h-[150px] overflow-y-auto p-3 rounded-lg border border-white/10 bg-black/30 
        font-mono text-xs text-[#e7e7e7]/90 whitespace-pre-wrap"
    >
      {isConnectingToLogs && logs.length === 0 ? (
        <div className="flex items-center justify-center h-full">
          <div className="i-ph:circle-notch w-5 h-5 animate-spin mr-2 text-[#e7e7e7]/70" />
          <span className="text-[#e7e7e7]/70">Connecting to logs...</span>
        </div>
      ) : logs.length === 0 ? (
        <div className="flex items-center justify-center h-full text-[#e7e7e7]/50">No logs available yet</div>
      ) : (
        logs.map((log, index) => (
          <div key={index} className="mb-1">
            <span className="text-[#e7e7e7]/50 mr-2">[{formatTimestamp(log.timestamp)}]</span> <br />
            <span
              className={`
                ${log.level === 'error' ? 'text-[#ef4444]' : ''}
                ${log.level === 'warn' ? 'text-[#f59e0b]' : ''}
                ${log.level === 'info' ? 'text-[#e7e7e7]' : ''}
                ${log.level === 'success' ? 'text-[#22c55e]' : ''}
              `}
              dangerouslySetInnerHTML={{ __html: formatLogMessage(log.message) }}
            />
          </div>
        ))
      )}
    </div>
  );

  // If no deployment is in progress, return null
  if (!deployedId || !accessToken) {
    return null;
  }

  // Show only the header with toggle and logs when expanded
  return (
    <div className="space-y-1">
      {/* Deployment Logs Header with Toggle */}
      <div className="flex items-center justify-between p-1">
        <div className="flex items-center gap-2 cursor-pointer py-1" onClick={toggleLogs}>
          <div className="i-ph:terminal w-4 h-4 text-[#e7e7e7]/70" />
          <h3 className="text-sm font-medium text-white m-0">Deployment Logs</h3>
        </div>
        <div className="flex items-center gap-1">
          <Tooltip content={isExpanded ? 'Collapse Logs' : 'Expand Logs'}>
            <button
              onClick={toggleLogs}
              className="p-1.5 text-[#e7e7e7]/70 hover:text-white bg-white/5 
                hover:bg-white/10 rounded-md transition-colors"
            >
              <div
                className={classNames(
                  'i-ph:caret-down-bold w-3.5 h-3.5 transition-transform',
                  isExpanded ? 'rotate-180' : '',
                )}
              />
            </button>
          </Tooltip>
        </div>
      </div>

      {/* Expanded Logs Panel */}
      {isExpanded && renderLogsContent()}
    </div>
  );
};
